import React, { useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';
import {
  AlertBanner,
  Box,
  Button,
  Chip,
  Icon,
  Loader,
  Menu,
  MenuItem,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import useGetIDProviders from '../../../hooks/use-getidproviders';
import useUpdateIDPProvider from '../../../hooks/use-updateidprovider';
import { type IDPConfig, type IDPConfigKosmos } from '../../../services/types';
import { useInvertedLocalize } from '../../../utils';
import { ERROR_MESSAGE } from '../../../utils/constants';
import { type Provider, PROVIDER_NAMES, providers } from '../constants';
import IDDataWebSettings from '../Requests/IDDatawebSettings/IDDatawebSettings';
import KosmosSettings from '../Requests/KosmosSettings/KosmosSettings';
import SetUpIdProofing from '../SetUpIdProofing/SetUpIdProofing';

import invertedMessages from './messages';

import './IDPProviders.css';

const initialMenuOptions = [
  {
    label: 'Settings',
    value: 'Settings',
  },
];

export type FormData = Record<string, string>;
export type ErrorMessages = Record<string, string>;

export type ProviderResponse = IDPConfig | IDPConfigKosmos;

const PROVIDERS_STATUS = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
} as const;

export default function IDPProviders() {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenEnableModal, setIsOpenEnableModal] = useState(false);
  const [isOpenDisableModal, setIsOpenDisableModal] = useState(false);
  const [currentProvider, setCurrentProvider] = useState<ProviderResponse>();
  const [isIDDataWeb, setIsIDDataWeb] = useState(false);
  const [isKosmos, setIsKosmos] = useState(false);
  const [isShowAlertBanner, setIsShowAlertBanner] = useState(false);
  const [isEnableSuccessVisible, setIsEnableSuccessVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertStatus, setAlertStatus] = useState('');
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [currentVendor, setCurrentVendor] = useState<Provider>();

  const [showCriticalBanner, setShowCriticalBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const l = useInvertedLocalize(invertedMessages);

  const {
    data,
    isLoading: isIDPProvidersLoading,
    refetch: refetchIDPProviders,
    isError,
    error,
  } = useGetIDProviders();

  const idpProviders = data;

  const { mutate: updateIDPConfig } = useUpdateIDPProvider();

  const handleConfigureClick = (provider: IDPConfig | IDPConfigKosmos) => {
    setCurrentProvider(provider);

    if (provider.vendorName === PROVIDER_NAMES.IDDATAWEB) {
      setIsIDDataWeb(true);
    } else if (provider.vendorName === PROVIDER_NAMES['1KOSMOS']) {
      setIsKosmos(true);
    }

    setIsOpen(true);
    setCurrentVendor(undefined);
    setShowSuccessBanner(false);
  };

  useEffect(() => {
    if (isError) {
      setErrorMessage(error.errorMessage ?? ERROR_MESSAGE);
      setShowCriticalBanner(true);
    }
  }, [isError, error]);

  const onCancel = () => {
    setIsEnableSuccessVisible(false);
  };

  const renderSuccessAlertBanner = (
    <Box className='Ext-IDPProviders-enable-successAlert'>
      <AlertBanner
        colorTheme='Success'
        title={`${alertTitle} ${alertStatus}`}
        description={
          alertStatus === 'Enabled'
            ? l(
                `You can now configure user modification workflow to include ID proofing workflow stage.`
              )
            : ''
        }
        isColonVisible={alertStatus === 'Enabled'}
        isVisible={isEnableSuccessVisible}
        onCancel={onCancel}
        shouldAutoDismiss
      />
    </Box>
  );

  const renderUpdateSuccessbanner = (
    <Box className='Ext-IDPProviders-enable-successAlert'>
      <AlertBanner
        colorTheme='Success'
        title={l(`Settings have been updated successfully.`)}
        description=''
        isColonVisible={alertStatus === 'Enabled'}
        isVisible={showSuccessBanner}
        onCancel={() => setShowSuccessBanner(false)}
        shouldAutoDismiss
        autoDismissDelay={3000}
      />
    </Box>
  );

  const renderErrorAlertBanner = (
    <Box className='Ext-IDPProviders-errorBanner'>
      <AlertBanner
        colorTheme='Critical'
        title={errorMessage}
        description=''
        isVisible={showCriticalBanner}
        onCancel={() => setShowCriticalBanner(false)}
        isColonVisible={false}
        isFloating
        position={{
          type: 'absolute',
          top: '7rem',
          left: '7rem',
        }}
        shouldAutoDismiss
      />
    </Box>
  );

  const enableModal = (provider: Provider, item: ProviderResponse) => (
    <Box className='Ext-IDPProviders-setupState'>
      <Button
        className='Ext-IDPProviders-setupState-button'
        type='button'
        kind='subtle'
        size='medium'
        onClick={() => setIsOpenEnableModal(true)}>
        Enable
      </Button>
      <ModalIsland
        kind='warning'
        alertTitle={l('Are you sure you want to enable ID proofing?')}
        alertSubtitle={l('Third party cost may be incurred.')}
        onClose={() => setIsOpenEnableModal(false)}
        isOpen={isOpenEnableModal}
        primaryButton={
          <Button
            type='button'
            kind='outlined'
            size='medium'
            onClick={() => {
              if (provider) {
                const providerConfig = item;

                providerConfig.status = 1;

                if (
                  providerConfig.config &&
                  'PASSWORD' in providerConfig.config
                ) {
                  delete providerConfig.config.PASSWORD;
                }

                updateIDPConfig(providerConfig, {
                  onSuccess(response) {
                    if (response.errorMessage) {
                      setErrorMessage(response.errorMessage);
                      setShowCriticalBanner(true);
                      setIsOpenEnableModal(false);
                    } else {
                      setIsEnableSuccessVisible(true);
                      setAlertTitle(provider.name);
                      setAlertStatus('Enabled');
                      setIsOpenEnableModal(false);
                    }
                  },
                });
              }
            }}>
            {l('Apply')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            onClick={() => setIsOpenEnableModal(false)}
            size='medium'>
            {l('Cancel')}
          </Button>
        }
      />
    </Box>
  );

  const disableModal = (
    provider: Provider,
    item: IDPConfig | IDPConfigKosmos
  ) => (
    <Box className='Ext-IDPProviders-setupState'>
      <Button
        className='Ext-IDPProviders-setupState-button'
        type='button'
        kind='subtle'
        size='medium'
        onClick={() => {
          setIsOpenDisableModal(true);
        }}>
        Disable
      </Button>
      <ModalIsland
        kind='warning'
        alertTitle={l('Are you sure you want to disable ID proofing?')}
        alertSubtitle=''
        onClose={() => setIsOpenDisableModal(false)}
        isOpen={isOpenDisableModal}
        primaryButton={
          <Button
            type='button'
            kind='outlined'
            size='medium'
            onClick={() => {
              if (provider) {
                const providerConfig = item;

                providerConfig.status = 0;

                if (
                  providerConfig.config &&
                  'PASSWORD' in providerConfig.config
                ) {
                  delete providerConfig.config.PASSWORD;
                }

                updateIDPConfig(providerConfig, {
                  onSuccess(response) {
                    if (response.errorMessage) {
                      setErrorMessage(response.errorMessage);
                      setShowCriticalBanner(true);
                      setIsOpenDisableModal(false);
                    } else {
                      setIsEnableSuccessVisible(true);
                      setAlertTitle(provider.name);
                      setAlertStatus('Disabled');
                      setIsOpenDisableModal(false);
                    }
                  },
                });
              }
            }}>
            {l('Disable')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            onClick={() => setIsOpenDisableModal(false)}
            size='medium'>
            {l('Cancel')}
          </Button>
        }
      />
    </Box>
  );

  const renderProviderCard = (item: IDPConfig | IDPConfigKosmos) => {
    const provider = providers.find((prov) => prov.id === item.vendorName);

    if (!provider) {
      return null;
    }

    return (
      <Box
        className='Ext-IDPProviders-contentWrapper-content-card'
        dataTestId={item.vendorName}
        key={item.vendorName}>
        <Box className='Ext-IDPProviders-contentWrapper-content-card-controller'>
          {item.status === 0 ? (
            <Chip size='xSmall' label={PROVIDERS_STATUS.DISABLED} />
          ) : (
            <Chip
              size='xSmall'
              color='success'
              label={PROVIDERS_STATUS.ENABLED}
            />
          )}
          <Menu
            className='Ext-IDPProviders-contentWrapper-content-card-controller-menu'
            CustomMenu={
              <>
                {initialMenuOptions.map((option) => (
                  <React.Fragment key={option.value}>
                    <MenuItem
                      key={option.value}
                      option={option}
                      onClick={() => {
                        handleConfigureClick(item);
                      }}
                      tabIndex={isOpen ? 0 : -1}
                    />
                  </React.Fragment>
                ))}
              </>
            }
            trigger={<Icon kind='MoreHorizontal' size='medium' />}
          />
        </Box>
        {provider && (
          <Icon
            kind={provider.icon}
            className='Ext-IDPProviders-contentWrapper-content-card-icon'
          />
        )}
        <Typography kind='h3' color='--color-foreground-neutral-bold'>
          {provider.name}
        </Typography>
        {item.status === 0
          ? provider && enableModal(provider, item)
          : provider && disableModal(provider, item)}
      </Box>
    );
  };

  return (
    <>
      {!isOpen && (
        <>
          <AlertBanner
            dataTestId='Ext-IDPProviders-alert'
            colorTheme='Critical'
            title={l('Data Fetch Error')}
            description={l(
              'ID Proofing configuration Action failed, please try again later.'
            )}
            isVisible={isShowAlertBanner}
            onCancel={() => setIsShowAlertBanner(false)}
            shouldAutoDismiss
          />
          {renderErrorAlertBanner}
          {renderUpdateSuccessbanner}
          {isIDPProvidersLoading && (
            <div className='Ext-IDPProviders-loadingContainer'>
              <Loader kind='loop' format='main' color='brand' size='large' />
            </div>
          )}
          {!isIDPProvidersLoading &&
            idpProviders &&
            idpProviders.length > 0 && (
              <Box className='Ext-IDPProviders'>
                <Box
                  className='Ext-IDPProviders-header'
                  dataTestId='Ext-IDPProviders-header'>
                  <Typography kind='h1' color='--color-foreground-neutral-bold'>
                    {l('ID Proofing')}
                  </Typography>
                </Box>
                {renderSuccessAlertBanner}
                <Box className='Ext-IDPProviders-contentWrapper'>
                  <div className='Ext-IDPProviders-contentWrapper-content'>
                    {idpProviders.map(renderProviderCard)}
                  </div>
                </Box>
              </Box>
            )}
          {!isIDPProvidersLoading && idpProviders?.length === 0 && (
            <SetUpIdProofing
              setIsOpen={setIsOpen}
              setCurrentVendor={setCurrentVendor}
              setIsIDDataWeb={setIsIDDataWeb}
              setIsKosmos={setIsKosmos}
            />
          )}
        </>
      )}
      <IDDataWebSettings
        isOpen={isOpen && isIDDataWeb}
        onClose={() => {
          setIsOpen(false);
          setIsIDDataWeb(false);
        }}
        provider={providers.find(
          (provider) => provider.id === currentProvider?.vendorName
        )}
        currentProvider={currentProvider as IDPConfig}
        currentVendor={currentVendor}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        refetchIDPProviders={refetchIDPProviders}
        setIsEnableSuccessVisible={setIsEnableSuccessVisible}
        setAlertTitle={setAlertTitle}
        setAlertStatus={setAlertStatus}
        setIsOpenEnableModal={setIsOpenEnableModal}
        setShowSuccessBanner={setShowSuccessBanner}
      />
      <KosmosSettings
        isOpen={isOpen && isKosmos}
        onClose={() => {
          setIsOpen(false);
          setIsKosmos(false);
          refetchIDPProviders().catch(Logger.error);
        }}
        provider={providers.find(
          (provider) => provider.id === currentProvider?.vendorName
        )}
        currentProvider={currentProvider as IDPConfigKosmos}
        currentVendor={currentVendor}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        refetchIDPProviders={refetchIDPProviders}
        setIsEnableSuccessVisible={setIsEnableSuccessVisible}
        setAlertTitle={setAlertTitle}
        setAlertStatus={setAlertStatus}
        setIsOpenEnableModal={setIsOpenEnableModal}
        setShowSuccessBanner={setShowSuccessBanner}
      />
    </>
  );
}
